import { motion, useReducedMotion } from "framer-motion";
import { Reveal, SectionTag, Counter, EASE } from "./motion";

const FUNNEL_CASE = [
  { label: "Leads", v: 759, w: "100%" },
  { label: "Agendamentos", v: 235, w: "44%" },
  { label: "Avaliações", v: 145, w: "30%" },
  { label: "Fechamentos", v: 85, w: "20%" },
];

export default function Results() {
  const reduce = useReducedMotion();

  return (
    <section id="resultados" data-testid="results-section" className="bg-ink py-28 lg:py-36 relative overflow-hidden">
      <div className="absolute top-1/3 left-0 w-[300px] h-[300px] lg:w-[500px] lg:h-[500px] rounded-full bg-gold/6 blur-[70px] lg:blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="max-w-3xl">
          <Reveal>
            <SectionTag>Resultados</SectionTag>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-bone leading-[1.12]" data-testid="results-title">
              Cases reais. <span className="text-gold">Números auditáveis.</span>
            </h2>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="results-counters">
          {[
            { to: 759, label: "Leads gerados em um case" },
            { to: 85, label: "Fechamentos no mesmo período" },
            { to: 720, prefix: "R$ ", suffix: " mil", label: "Em vendas geradas (2 cases)" },
            { to: 66, suffix: "x", label: "Retorno sobre mídia" },
          ].map((c, i) => (
            <Reveal key={c.label} delay={i * 0.08}>
              <div className="border-l-2 border-gold/50 pl-5 py-1">
                <p className="font-display font-extrabold text-3xl lg:text-4xl text-gold">
                  <Counter
                    to={c.to}
                    format={(v) => `${c.prefix || ""}${Math.round(v).toLocaleString("pt-BR")}${c.suffix || ""}`}
                  />
                </p>
                <p className="mt-2 text-xs uppercase tracking-widest text-bone/50">{c.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div data-testid="case-card-1" className="h-full bg-graphite/60 border border-gold/20 rounded-3xl p-8 lg:p-10">
              <p className="text-xs uppercase tracking-[0.28em] text-gold font-semibold">Case 01 — Mídia & Vendas</p>
              <div className="mt-8 space-y-6">
                <div>
                  <div className="flex justify-between text-sm text-bone/60 mb-2">
                    <span>Investido em mídia</span>
                    <span className="font-display font-bold text-bone">R$ 4.300</span>
                  </div>
                  <div className="h-3 bg-ink rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "6%" }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, ease: EASE }}
                      className="h-full bg-bone/40 rounded-full"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm text-bone/60 mb-2">
                    <span>Vendas geradas</span>
                    <span className="font-display font-bold text-gold">
                      R$ <Counter to={285} format={(v) => Math.round(v)} /> mil
                    </span>
                  </div>
                  <div className="h-3 bg-ink rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.4, ease: EASE, delay: 0.3 }}
                      className="h-full bg-gradient-to-r from-gold-deep to-gold rounded-full"
                    />
                  </div>
                </div>
              </div>
              <p className="mt-8 font-display font-bold text-xl text-bone">
                Cada R$ 1 em mídia retornou <span className="text-gold">R$ 66 em vendas.</span>
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div data-testid="case-card-2" className="h-full bg-graphite/60 border border-gold/20 rounded-3xl p-8 lg:p-10">
              <p className="text-xs uppercase tracking-[0.28em] text-gold font-semibold">Case 02 — Funil completo</p>
              <div className="mt-8 space-y-4">
                {FUNNEL_CASE.map((r, i) => (
                  <div key={r.label}>
                    <div className="flex justify-between text-sm text-bone/60 mb-1.5">
                      <span>{r.label}</span>
                      <span className="font-display font-bold text-bone">{r.v}</span>
                    </div>
                    <div className="h-2.5 bg-ink rounded-full overflow-hidden">
                      <motion.div
                        initial={reduce ? false : { width: 0 }}
                        whileInView={{ width: r.w }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: i * 0.15, ease: EASE }}
                        className="h-full bg-gradient-to-r from-gold-deep to-gold rounded-full"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-8 font-display font-bold text-xl text-bone">
                <span className="text-gold">R$ 435 mil</span> em vendas a partir de 759 leads.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <p className="mt-10 text-xs text-bone/35 max-w-2xl" data-testid="results-disclaimer">
            Resultados de cases reais atendidos pela Pulse Saúde. Desempenho passado não constitui
            garantia de resultados futuros — cada clínica tem mercado, operação e momento próprios.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
